import React, { useState } from 'react'
import { Skeleton } from 'antd'
import { GoogleMap, InfoWindow, Marker, Polyline } from '@react-google-maps/api'
import { useGoogleMaps } from '../../utils/googleMapsLoader'
import TraceEndpoints from '../TraceEndpoints/TraceEndpoints'

// One journey of the daily route history: the bus's road-snapped trace in red
// (OSRM map-matched when available, raw GPS otherwise), the matched trip's
// official geometry in blue with its stops (name + scheduled time on click),
// fitted to all three. Shared by Vehicle Details and the Route History page.
const RouteHistoryMap = ({ trace, shape, stops, height = '300px' }) => {
    const { isLoaded } = useGoogleMaps()
    const [selectedStop, setSelectedStop] = useState(null)
    if (!isLoaded) return <Skeleton active paragraph={{ rows: 4 }} />
    const points = [...(trace || []), ...(shape || []), ...(stops || [])]
    // Direction of travel — same arrow style as the other maps (VehicleMap /
    // main map): FORWARD_CLOSED_ARROW symbols repeated along the polyline.
    const directionArrows = window.google?.maps
        ? [
              {
                  icon: {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 3,
                      strokeColor: '#FF0000',
                  },
                  offset: '0%',
                  repeat: '200px',
              },
          ]
        : []
    return (
        <GoogleMap
            mapContainerStyle={{ width: '100%', height }}
            onLoad={(map) => {
                if (points.length && window.google?.maps) {
                    const bounds = new window.google.maps.LatLngBounds()
                    points.forEach((p) => bounds.extend(p))
                    map.fitBounds(bounds)
                }
            }}
            options={{ disableDefaultUI: true, gestureHandling: 'greedy' }}
        >
            {shape?.length > 1 && (
                <Polyline
                    path={shape}
                    options={{ strokeColor: '#4285F4', strokeWeight: 4, strokeOpacity: 0.9 }}
                />
            )}
            {trace?.length > 1 && (
                <Polyline
                    path={trace}
                    options={{
                        strokeColor: '#FF0000',
                        strokeWeight: 3,
                        strokeOpacity: 1,
                        icons: directionArrows,
                    }}
                />
            )}
            {/* Where the journey started (green, S) and ended (red, E). */}
            <TraceEndpoints path={trace} />
            {/* The matched trip's stops — same palette as the main map. */}
            {(stops || [])
                .filter((s) => Number.isFinite(s?.lat) && Number.isFinite(s?.lng))
                .map((s) => (
                    <Marker
                        key={`stop-${s.gtfs_stop_id}-${s.sequence}`}
                        position={{ lat: s.lat, lng: s.lng }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 5,
                            fillColor: '#1a73e8',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        }}
                        zIndex={999}
                        onClick={() => setSelectedStop(s)}
                    />
                ))}
            {selectedStop && (
                <InfoWindow
                    position={{ lat: selectedStop.lat, lng: selectedStop.lng }}
                    onCloseClick={() => setSelectedStop(null)}
                >
                    <div>
                        <div>
                            <strong>{selectedStop.name}</strong>
                        </div>
                        {selectedStop.scheduled && (
                            <div>
                                🕒 <strong>{selectedStop.scheduled}</strong>
                            </div>
                        )}
                    </div>
                </InfoWindow>
            )}
        </GoogleMap>
    )
}

export default RouteHistoryMap
